import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger 
} from "@/components/ui/tooltip";
import { getIcon } from "./iconMap";

export interface DropdownItem {
  to: string;
  label: string;
  icon: string;
  roles?: (string | null)[];
  section?: string;
}

interface SidebarDropdownProps {
  label: string;
  icon: string;
  items: DropdownItem[];
  collapsed: boolean;
}

export const SidebarDropdown: React.FC<SidebarDropdownProps> = ({ 
  label, 
  icon, 
  items, 
  collapsed 
}) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  
  const isAnyItemActive = items.some(item => location.pathname.startsWith(item.to));
  
  // Auto-open se algum item estiver ativo
  React.useEffect(() => {
    if (isAnyItemActive && !collapsed) {
      setIsOpen(true);
    }
  }, [isAnyItemActive, collapsed]);

  const IconComponent = getIcon(icon);

  const handleToggle = () => {
    if (!collapsed) {
      setIsOpen(!isOpen);
    }
  };

  const isItemActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const dropdownButton = (
    <button
      onClick={handleToggle}
      className={`w-full flex items-center gap-3 px-3 py-2.5 mb-1 rounded-lg transition-all ${
        isAnyItemActive 
          ? "bg-gradient-to-r from-[#182437] to-[#1E293B] text-[#00B6F0] border-l-4 border-[#00B6F0]" 
          : "text-gray-400 hover:text-white hover:bg-[#1A2233]"
      }`}
    >
      <span className={`${isAnyItemActive ? "text-[#00B6F0]" : "text-gray-400"}`}>
        <IconComponent className="w-5 h-5" />
      </span>
      {!collapsed && (
        <>
          <span className="font-medium truncate flex-1 text-left">{label}</span>
          <span className={`${isAnyItemActive ? "text-[#00B6F0]" : "text-gray-400"}`}>
            {isOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </span>
        </>
      )}
      {isAnyItemActive && !collapsed && (
        <span className="ml-auto h-2 w-2 rounded-full bg-[#00B6F0]"></span>
      )}
    </button>
  );

  if (collapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {dropdownButton}
          </TooltipTrigger>
          <TooltipContent side="right">
            <div className="space-y-1">
              <div className="font-medium">{label}</div>
              {items.map((item) => (
                <div key={item.to} className="text-sm text-gray-300">
                  {item.label}
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div>
      {dropdownButton}
      
      {isOpen && !collapsed && (
        <div className="ml-6 mt-1 space-y-1">
          {items.map((item) => {
            const ItemIcon = getIcon(item.icon);
            const isActive = isItemActive(item.to);
            
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                  isActive 
                    ? "bg-gradient-to-r from-[#182437] to-[#1E293B] text-[#00B6F0] border-l-2 border-[#00B6F0]" 
                    : "text-gray-400 hover:text-white hover:bg-[#1A2233]"
                }`}
              >
                <span className={`${isActive ? "text-[#00B6F0]" : "text-gray-400"}`}>
                  <ItemIcon className="w-4 h-4" />
                </span>
                <span className="font-medium truncate text-sm">{item.label}</span>
                {isActive && (
                  <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#00B6F0]"></span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}; 